import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

type ConfirmBeforeLeavingOptions = {
  hasUnsavedChanges: () => boolean;
};

export const useConfirmBeforeLeaving = ({ hasUnsavedChanges }: ConfirmBeforeLeavingOptions) => {
  const history = useHistory();
  const { t } = useTranslation();

  // Read at navigation time, not at registration time: a save that clears the form
  // and navigates in the same tick gets no re-render in between.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    const unblock = history.block((nextLocation, action) => {
      if (!hasUnsavedChangesRef.current()) return undefined;

      modals.openConfirmModal({
        title: t(i18nKeys.ATTENTION),
        children: <Text size="sm">{t(i18nKeys.AYS.EXIT_PAGE)}</Text>,
        labels: {
          confirm: t(i18nKeys.FORM.QUIT_WITHOUT_SAVING),
          cancel: t(i18nKeys.CANCEL),
        },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          unblock();
          if (action === 'POP') history.goBack();
          else if (action === 'REPLACE') history.replace(nextLocation);
          else history.push(nextLocation);
        },
      });

      return false;
    });

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current()) event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeUnload);

    return () => {
      unblock();
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [history, t]);
};
